import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Spin,
  Empty,
  Collapse,
  Table,
  Tag,
  Space,
  Typography,
} from "antd";
import { TeamOutlined, WarningOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getGroupMemberIdentitiesWithComments,
  getGroupMemberGroupsWithComments,
} from "../../actions/api";
import { showErrorNotification } from "../../util";
import { types, errorAction } from "@authzsvc/api-lib";
import LabeledSwitch from "../common/LabeledSwitch";

const FlattenedGroupMembership = () => {
  const dispatch = useDispatch();
  const selectedGroup = useSelector((state) => state.groups.selectedGroup);

  const [loading, setLoading] = useState(false);
  const [flattenedData, setFlattenedData] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);

  const expandableKeys = useMemo(
    () =>
      flattenedData
        .filter(
          (group) => group.errors.length === 0 && group.members.length > 0
        )
        .map((group) => group.groupId),
    [flattenedData]
  );

  /**
   * Get the display email for an identity member
   * Prioritizes mailAddress override over primary email
   */
  const getDisplayEmail = (member) => {
    if (member.emailAddress) {
      return member.emailAddress;
    }
    return member.memberIdentity?.email || "";
  };

  /**
   * Recursively fetch and flatten group membership hierarchy
   */
  const fetchGroupHierarchy = useCallback(
    async (groupId, groupIdentifier, parentPath = []) => {
      const currentPath = [...parentPath, groupId];
      const result = [];

      try {
        console.log("Looking up members for ", groupId);
        // Fetch identity members for this group
        const identitiesResponse = await dispatch(
          getGroupMemberIdentitiesWithComments(groupId, {})
        );

        if (
          identitiesResponse.type !==
          errorAction(types.GET_GROUP_MEMBER_IDENTITIES_WITH_COMMENTS)
        ) {
          const identities = identitiesResponse.payload?.data || [];
          identities.forEach((member) => {
            result.push({
              type: "identity",
              groupId,
              groupIdentifier,
              id: member.memberIdentity?.id,
              displayName: member.memberIdentity?.displayName,
              email: getDisplayEmail(member),
              comment: member.comment,
              member_type: member.memberIdentity.type,
              upn: member.memberIdentity?.upn,
            });
          });
          if (identities.length === 0) {
            result.push({
              type: "warning",
              groupId,
              groupIdentifier,
              message: "No direct member identities",
            });
          }
          if (identities.length === 1000) {
            result.push({
              type: "warning",
              groupId,
              groupIdentifier,
              message: "Only first 1000 displayed",
            });
          }
        }
      } catch (error) {
        result.push({
          type: "error",
          groupId,
          groupIdentifier,
          message: "Failed to read identity members",
        });
      }

      // Fetch group members for this group
      try {
        const groupsResponse = await dispatch(
          getGroupMemberGroupsWithComments(groupId, {})
        );
        if (
          groupsResponse.type !==
          errorAction(types.GET_GROUP_MEMBER_GROUPS_WITH_COMMENTS)
        ) {
          const groups = groupsResponse.payload?.data || [];
          console.log("Found group members ", groups);
          // Recursively fetch each child group's members
          for (const childGroup of groups) {
            // Prevent circular references
            console.log("processing group member ", childGroup);
            if (!currentPath.includes(childGroup.memberGroup.id)) {
              const childResults = await fetchGroupHierarchy(
                childGroup.memberGroup.id,
                childGroup.memberGroup.groupIdentifier,
                currentPath
              );
              result.push(...childResults);
            }
          }
        } else {
          result.push({
            type: "error",
            groupId,
            groupIdentifier,
            message: "do not have permissions to read membership",
          });
        }
      } catch (error) {
        result.push({
          type: "error",
          groupId,
          groupIdentifier,
          message: "do not have permissions to read membership",
        });
      }

      return result;
    },
    [dispatch]
  );

  /**
   * Organize flattened data by group for display
   */
  const organizeByGroup = (flatData) => {
    const grouped = {};

    flatData.forEach((item) => {
      if (!grouped[item.groupId]) {
        grouped[item.groupId] = {
          groupId: item.groupId,
          groupIdentifier: item.groupIdentifier,
          members: [],
          errors: [],
          warnings: [],
        };
      }

      if (item.type === "error") {
        grouped[item.groupId].errors.push(item.message);
      } else if (item.type === "warning") {
        grouped[item.groupId].warnings.push(item);
      } else if (item.type === "identity") {
        grouped[item.groupId].members.push(item);
      }
    });

    return Object.values(grouped);
  };

  /**
   * Load flattened group membership data
   */
  const loadFlattenedMembership = useCallback(async () => {
    if (!selectedGroup?.id) return;

    setLoading(true);
    setActiveKeys([]);
    try {
      const data = await fetchGroupHierarchy(
        selectedGroup.id,
        selectedGroup.groupIdentifier
      );
      const organized = organizeByGroup(data);
      setFlattenedData(organized);
    } catch (error) {
      showErrorNotification(
        "Failed to load flattened membership",
        "An error occurred while loading the group hierarchy"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, fetchGroupHierarchy]);

  useEffect(() => {
    loadFlattenedMembership();
  }, [loadFlattenedMembership]);

  /**
   * Render table for group members
   */
  const renderMembersTable = (members) => {
    const columns = [
      {
        title: "Display Name",
        dataIndex: "displayName",
        key: "displayName",
        render: (text, record) => (
          <span title={record.upn}>{text || record.upn}</span>
        ),
      },
      {
        title: "Identifier",
        dataIndex: "upn",
        key: "upn",
        render: (text, record) => (
          <span title={record.upn}>{text || record.upn}</span>
        ),
      },
      {
        title: "Effective Email",
        dataIndex: "email",
        key: "email",
        render: (text) => text || "-",
      },
      {
        title: "Type",
        dataIndex: "member_type",
        key: "type",
        render: (text, record) => (
          <span title={record.member_type}>{text || record.member_type}</span>
        ),
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        render: (text) => text || "-",
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={members}
        rowKey={(record) => record.id}
        pagination={false}
        size="small"
        style={{ marginTop: 12 }}
      />
    );
  };

  /**
   * Render collapse items for each group
   */
  const renderCollapseItems = () => {
    return flattenedData.map((groupData) => {
      const hasErrors = groupData.errors.length > 0;
      const hasWarnings = groupData.warnings.length > 0;
      const memberCount = groupData.members.length;
      const hasChildren = !hasErrors && memberCount > 0;

      return {
        key: groupData.groupId,
        label: (
          <Space>
            <TeamOutlined />
            <span>{groupData.groupIdentifier}</span>
            {memberCount > 0 && <Tag color="blue">{memberCount} members</Tag>}
            {hasErrors && (
              <Tag color="orange" icon={<WarningOutlined />}>
                Permissions Issue
              </Tag>
            )}
            {hasWarnings &&
              groupData.warnings?.map((warning, index) => (
                <Tag
                  color="yellow"
                  key={`${warning}-${index}`}
                  icon={<WarningOutlined />}
                >
                  {warning.message}
                </Tag>
              ))}
          </Space>
        ),
        collapsible: hasChildren ? undefined : "disabled",
        showArrow: hasChildren,
        children: hasChildren ? renderMembersTable(groupData.members) : null,
      };
    });
  };

  if (loading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "50px" }}
      />
    );
  }

  if (flattenedData.length === 0) {
    return <Empty description="No nested groups or members found" />;
  }

  return (
    <Card>
      <Typography.Paragraph>
        This view shows all direct members from this group, and from all nested
        groups for which you have permissions to view the members. It is
        provided to help with membership reviews. If you require full visibility
        and control of all group members we strongly advise only including
        groups for which you have administrative permissions.
      </Typography.Paragraph>
      <div style={{ marginBottom: 16 }}>
        <LabeledSwitch
          checked={
            expandableKeys.length > 0 &&
            activeKeys.length === expandableKeys.length
          }
          disabled={expandableKeys.length === 0}
          onChange={(checked) => {
            setActiveKeys(checked ? expandableKeys : []);
          }}
          label="Expand all"
        />
      </div>
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => {
          setActiveKeys(Array.isArray(keys) ? keys : [keys].filter(Boolean));
        }}
        items={renderCollapseItems()}
      />
    </Card>
  );
};

export default FlattenedGroupMembership;
